import { Currency } from "@redotech/money/currencies";
import {
  AsendiaServices,
  AustraliaPostServices,
  FirstMileServices,
  GlsServices,
  LandmarkGlobalServices,
  OnTracServices,
  PassportGlobalServices,
  RoyalMailV3Services,
  VehoServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  SpeeDeeServices,
  UpsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { DhlEcsDistributionCenter } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { LengthUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  FedexRateTableServiceLevel,
  USPSRateTableServiceLevel,
  USPSReturnsRateTableServiceLevel,
} from "./rate-table-mapping";

export enum AxisKind {
  ZONE = "zone",
  WEIGHT = "weight",
  CUBIC_SIZE = "cubic_size",
  PACKAGE = "package",
  COUNTRY = "country",
}

/** Lives in the yAxis JSONB payload, so there is no DB enum to source from. */
export enum WeightUnit {
  OZ = "OZ",
  LB = "LB",
  KG = "KG",
}

export enum RateTableType {
  OUTBOUND_WEIGHT_BY_ZONE = "outbound_weight_by_zone",
  OUTBOUND_SIZE_BY_ZONE = "outbound_size_by_zone",
  RETURNS_WEIGHT_BY_ZONE = "returns_weight_by_zone",
  RETURNS_DEFAULT_WEIGHT_BY_ZONE = "returns_default_weight_by_zone",
  RETURNS_DEFAULT_BY_WEIGHT = "returns_default_by_weight",
  RETURNS_TEAM_WEIGHT_BY_ZONE = "returns_team_weight_by_zone",
  RETURNS_TEAM_BY_WEIGHT = "returns_team_by_weight",
  OUTBOUND_PACKAGE_BY_ZONE = "outbound_package_by_zone",
  OUTBOUND_WEIGHT_BY_COUNTRY = "outbound_weight_by_country",
  OUTBOUND_SIZE_BY_COUNTRY = "outbound_size_by_country",
  OUTBOUND_PACKAGE_BY_COUNTRY = "outbound_package_by_country",
  RETURNS_DEFAULT_WEIGHT_BY_COUNTRY = "returns_default_weight_by_country",
  RETURNS_TEAM_WEIGHT_BY_COUNTRY = "returns_team_weight_by_country",
}

export type OutboundRateTableType =
  | RateTableType.OUTBOUND_WEIGHT_BY_ZONE
  | RateTableType.OUTBOUND_SIZE_BY_ZONE
  | RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY
  | RateTableType.OUTBOUND_SIZE_BY_COUNTRY;

// Are the values meant to be used as the values we charge the customer (rate) or are they the margins that we add to the base rate (spread)
export enum RateTableValueMode {
  /** The table represents the values we charge */
  Rate = "Rate",
  /** The table represents the margins that we add to the base rate */
  Spread = "Spread",
  /** The table represents the percentage markup applied to the base rate */
  SpreadPercent = "SpreadPercent",
  /** The table represents the percentage discount off of the base rate */
  Discount = "Discount",
}

const zoneAxisSchema = z.object({
  kind: z.literal(AxisKind.ZONE).meta({ privacy: privacy.nonPii }),
  values: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.structured }),
});

const weightAxisSchema = z.object({
  kind: z.literal(AxisKind.WEIGHT).meta({ privacy: privacy.nonPii }),
  units: createEnum(WeightUnit, { enumName: "rate_table_weight_unit" }).meta({
    privacy: privacy.nonPii,
  }),
  values: z
    .array(
      z
        .object({
          minWeight: z.number().meta({ privacy: privacy.nonPii }),
          maxWeight: z.number().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
});

const cubicSizeAxisSchema = z.object({
  kind: z.literal(AxisKind.CUBIC_SIZE).meta({ privacy: privacy.nonPii }),
  units: createEnum(LengthUnit, { enumName: "rate_table_length_unit" }).meta({
    privacy: privacy.nonPii,
  }),
  values: z
    .array(
      z
        .object({
          minCubicSize: z.number().meta({ privacy: privacy.nonPii }),
          maxCubicSize: z.number().meta({ privacy: privacy.nonPii }),
        })
        .meta({ privacy: privacy.structured }),
    )
    .meta({ privacy: privacy.structured }),
});

const packageAxisSchema = z.object({
  kind: z.literal(AxisKind.PACKAGE).meta({ privacy: privacy.nonPii }),
  // This JSON path also stores numeric objects; a public scalar rule would release their unlisted descendants.
  values: z
    .array(z.string().meta({ privacy: privacy.untypedPayload }))
    .meta({ privacy: privacy.structured }),
});

const countryAxisSchema = z.object({
  kind: z.literal(AxisKind.COUNTRY).meta({ privacy: privacy.nonPii }),
  values: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .meta({ privacy: privacy.structured }),
});

export function formatCountryColumn(
  country: string,
  province?: string | null,
): string {
  return province ? `${country}-${province}` : country;
}

export function parseCountryColumn(value: string): {
  country: string;
  province?: string;
} {
  const separatorIndex = value.indexOf("-");
  return separatorIndex === -1
    ? { country: value }
    : {
        country: value.slice(0, separatorIndex),
        province: value.slice(separatorIndex + 1),
      };
}

// Service level enum params - all carrier enums share the same enumName
// so the zod-extractor merges all values into a single enum for DDL generation
const serviceLevelEnumParams = {
  enumName: "rate_table_service_level",
} as const;

// Flat union of all carrier service enums - the extractor merges them by enumName
export const serviceLevelsSchema = z
  .union([
    createEnum(USPSRateTableServiceLevel, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(USPSReturnsRateTableServiceLevel, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(FedexRateTableServiceLevel, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(UpsServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AmazonServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlExpressServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(VehoServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(FirstMileServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AustraliaPostServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(CanadaPostServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(DhlEcsServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(ApcServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(GlsServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(AsendiaServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(RoyalMailV3Services, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(SpeeDeeServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(PassportGlobalServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(LandmarkGlobalServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(OnTracServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(EPostGlobalServices, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
    createEnum(["Default"] as const, serviceLevelEnumParams).meta({
      privacy: privacy.nonPii,
    }),
  ])
  .meta({ privacy: privacy.nonPii });

const _baseSchema = z.object({
  version: z.number().nullish().meta({ privacy: privacy.nonPii }),
  rateTableType: createEnum(RateTableType, {
    enumName: "rate_table_schema_type",
  }).meta({ privacy: privacy.nonPii }),
  valueMode: createEnum(RateTableValueMode, {
    enumName: "rate_table_value_mode",
  }).meta({ privacy: privacy.nonPii }),
  data: z
    .array(
      z
        .array(z.string().meta({ privacy: privacy.nonPii }))
        .meta({ privacy: privacy.nonPii }),
    )
    .meta({ privacy: privacy.structured }),
  defaultValue: z.string().meta({ privacy: privacy.nonPii }),
  defaultValuePercentage: z
    .number()
    .min(0)
    .max(100)
    .nullish()
    .meta({ privacy: privacy.nonPii }),
  useDefaultValuePercentage: z
    .boolean()
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});

const _baseOmsSchema = _baseSchema.extend({
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).meta({
    privacy: privacy.nonPii,
  }),
  carrier: createEnum(RateTableCarrier, {
    enumName: "rate_table_carrier",
  }).meta({ privacy: privacy.nonPii }),
  serviceLevel: serviceLevelsSchema.meta({ privacy: privacy.nonPii }),
  distributionCenterId: createEnum(DhlEcsDistributionCenter, {
    enumName: "dhl_ecs_distribution_center",
  })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
});

export const outboundWeightByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_WEIGHT_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
});

const outboundCubicSizeByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_SIZE_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: cubicSizeAxisSchema.meta({ privacy: privacy.structured }),
});

const outboundPackageByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_PACKAGE_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: packageAxisSchema.meta({ privacy: privacy.structured }),
});

export const outboundWeightByCountrySchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY)
    .meta({ privacy: privacy.nonPii }),
  xAxis: countryAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
});

export const outboundCubicSizeByCountrySchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_SIZE_BY_COUNTRY)
    .meta({ privacy: privacy.nonPii }),
  xAxis: countryAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: cubicSizeAxisSchema.meta({ privacy: privacy.structured }),
});

export const outboundPackageByCountrySchema = _baseOmsSchema.extend({
  rateTableType: z
    .literal(RateTableType.OUTBOUND_PACKAGE_BY_COUNTRY)
    .meta({ privacy: privacy.nonPii }),
  xAxis: countryAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: packageAxisSchema.meta({ privacy: privacy.structured }),
});

export const returnsWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_WEIGHT_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }).meta({
    privacy: privacy.nonPii,
  }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
});

export const returnsDefaultWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_DEFAULT_WEIGHT_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
  carrier: createEnum(RateTableCarrier, {
    enumName: "rate_table_carrier",
  }).meta({ privacy: privacy.nonPii }),
  serviceLevel: serviceLevelsSchema.meta({ privacy: privacy.nonPii }),
});

export const returnsDefaultDiscountByWeightSchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_DEFAULT_BY_WEIGHT)
    .meta({ privacy: privacy.nonPii }),
  // yAxis-only rate table: discount is uniform across weights via
  // defaultValue, so xAxis is unused. Optional rather than .default(...) so
  // the CongoDB schema-extractor emits a nullable x_axis_kind column —
  // .default() keeps NOT NULL semantics, which broke the backfill on docs
  // that pre-date the default. See INFRA-1157.
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }).optional(),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
  carrier: createEnum(RateTableCarrier, {
    enumName: "rate_table_carrier",
  }).meta({ privacy: privacy.nonPii }),
  serviceLevel: serviceLevelsSchema.meta({ privacy: privacy.nonPii }),
});

export const returnsTeamWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_TEAM_WEIGHT_BY_ZONE)
    .meta({ privacy: privacy.nonPii }),
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
});

export const returnsTeamDiscountByWeightSchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_TEAM_BY_WEIGHT)
    .meta({ privacy: privacy.nonPii }),
  // yAxis-only rate table: see returnsDefaultDiscountByWeightSchema above.
  xAxis: zoneAxisSchema.meta({ privacy: privacy.structured }).optional(),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
});

// Intl returns market-rate cards: weight rows x destination-country columns.
// Lane scoping lives on the identity via `origins`, not a third axis.
const returnsCountryFieldsSchema = {
  xAxis: countryAxisSchema.meta({ privacy: privacy.structured }),
  yAxis: weightAxisSchema.meta({ privacy: privacy.structured }),
  // ISO codes and/or region tokens ("EU"); null/empty = any origin. Identity
  // field: save by _id with disjointness validation, never natural-key upsert.
  origins: z
    .array(z.string().meta({ privacy: privacy.nonPii }))
    .nullish()
    .meta({ privacy: privacy.structured }),
  // Authoring currency of the cells; absent = USD. Converted at lookup.
  currency: createEnum(Currency, { enumName: "Currency" })
    .nullish()
    .meta({ privacy: privacy.nonPii }),
};

export const returnsDefaultWeightByCountrySchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_DEFAULT_WEIGHT_BY_COUNTRY)
    .meta({ privacy: privacy.nonPii }),
  ...returnsCountryFieldsSchema,
  carrier: createEnum(RateTableCarrier, {
    enumName: "rate_table_carrier",
  }).meta({ privacy: privacy.nonPii }),
  serviceLevel: serviceLevelsSchema.meta({ privacy: privacy.nonPii }),
});

export const returnsTeamWeightByCountrySchema = _baseSchema.extend({
  rateTableType: z
    .literal(RateTableType.RETURNS_TEAM_WEIGHT_BY_COUNTRY)
    .meta({ privacy: privacy.nonPii }),
  ...returnsCountryFieldsSchema,
});

export const rateTableSchema = z
  .discriminatedUnion("rateTableType", [
    outboundWeightByZoneSchema.meta({ privacy: privacy.structured }),
    outboundCubicSizeByZoneSchema.meta({ privacy: privacy.structured }),
    outboundPackageByZoneSchema.meta({ privacy: privacy.structured }),
    outboundWeightByCountrySchema.meta({ privacy: privacy.structured }),
    outboundCubicSizeByCountrySchema.meta({ privacy: privacy.structured }),
    outboundPackageByCountrySchema.meta({ privacy: privacy.structured }),
    returnsWeightByZoneSchema.meta({ privacy: privacy.structured }),
    returnsDefaultWeightByZoneSchema.meta({ privacy: privacy.structured }),
    returnsDefaultDiscountByWeightSchema.meta({ privacy: privacy.structured }),
    returnsTeamWeightByZoneSchema.meta({ privacy: privacy.structured }),
    returnsTeamDiscountByWeightSchema.meta({ privacy: privacy.structured }),
    returnsDefaultWeightByCountrySchema.meta({ privacy: privacy.structured }),
    returnsTeamWeightByCountrySchema.meta({ privacy: privacy.structured }),
  ])
  .refine(
    ({ data, yAxis }) => data.length === yAxis.values.length,
    "Invalid number of rows",
  )
  .refine(({ data, xAxis }) => {
    if (!xAxis) {
      return data.every((row) => row.length === 0);
    }
    if (xAxis.kind === AxisKind.COUNTRY) {
      // The trailing rest-of-world column is opt-in: a card without it
      // explicitly quotes only its listed destinations.
      const withoutRestOfWorld = xAxis.values.length;
      const withRestOfWorld = xAxis.values.length + 1;
      return (
        data.every((row) => row.length === withoutRestOfWorld) ||
        data.every((row) => row.length === withRestOfWorld)
      );
    }
    return data.every((row) => row.length === xAxis.values.length);
  }, "Invalid number of columns");

export const RateTableDocumentSchema = rateTableSchema.and(
  z.object({
    team: zExt.objectId("teams").optional().meta({ privacy: privacy.nonPii }),
    createdAt: z.date().optional().meta({ privacy: privacy.nonPii }),
    updatedAt: z.date().optional().meta({ privacy: privacy.nonPii }),
  }),
);

export type OutboundCubicSizeByZoneRateTable = z.infer<
  typeof outboundCubicSizeByZoneSchema
>;

export type OutboundWeightByZoneRateTable = z.infer<
  typeof outboundWeightByZoneSchema
>;

export type OutboundPackageByZoneRateTable = z.infer<
  typeof outboundPackageByZoneSchema
>;

export type OutboundWeightByCountryRateTable = z.infer<
  typeof outboundWeightByCountrySchema
>;

export type OutboundCubicSizeByCountryRateTable = z.infer<
  typeof outboundCubicSizeByCountrySchema
>;

export type OutboundPackageByCountryRateTable = z.infer<
  typeof outboundPackageByCountrySchema
>;

export type OutboundRateTable =
  | OutboundCubicSizeByZoneRateTable
  | OutboundWeightByZoneRateTable
  | OutboundPackageByZoneRateTable
  | OutboundWeightByCountryRateTable
  | OutboundCubicSizeByCountryRateTable
  | OutboundPackageByCountryRateTable;

export type ReturnsWeightByZoneRateTable = z.infer<
  typeof returnsWeightByZoneSchema
>;

export type ReturnsDefaultWeightByZoneRateTable = z.infer<
  typeof returnsDefaultWeightByZoneSchema
>;

export type ReturnsDefaultDiscountByWeightTable = z.infer<
  typeof returnsDefaultDiscountByWeightSchema
>;

export type ReturnsTeamWeightByZoneRateTable = z.infer<
  typeof returnsTeamWeightByZoneSchema
>;

export type ReturnsTeamDiscountByWeightTable = z.infer<
  typeof returnsTeamDiscountByWeightSchema
>;

export type ReturnsDefaultWeightByCountryRateTable = z.infer<
  typeof returnsDefaultWeightByCountrySchema
>;

export type ReturnsTeamWeightByCountryRateTable = z.infer<
  typeof returnsTeamWeightByCountrySchema
>;

export type WeightByZoneRateTable =
  | OutboundWeightByZoneRateTable
  | ReturnsWeightByZoneRateTable;

export function isOutboundWeightByZoneRateTable(
  table: RateTable,
): table is OutboundWeightByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_WEIGHT_BY_ZONE;
}

export function isOutboundCubicSizeByZoneRateTable(
  table: RateTable,
): table is OutboundCubicSizeByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_SIZE_BY_ZONE;
}

function isOutboundPackageByZoneRateTable(
  table: RateTable,
): table is OutboundPackageByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_PACKAGE_BY_ZONE;
}

export function isOutboundWeightByCountryRateTable(
  table: RateTable,
): table is OutboundWeightByCountryRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY;
}

export function isOutboundCubicSizeByCountryRateTable(
  table: RateTable,
): table is OutboundCubicSizeByCountryRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_SIZE_BY_COUNTRY;
}

function isOutboundPackageByCountryRateTable(
  table: RateTable,
): table is OutboundPackageByCountryRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_PACKAGE_BY_COUNTRY;
}

export function isOutboundRateTable(
  table: RateTable,
): table is OutboundRateTable {
  return (
    isOutboundWeightByZoneRateTable(table) ||
    isOutboundCubicSizeByZoneRateTable(table) ||
    isOutboundPackageByZoneRateTable(table) ||
    isOutboundWeightByCountryRateTable(table) ||
    isOutboundCubicSizeByCountryRateTable(table) ||
    isOutboundPackageByCountryRateTable(table)
  );
}

export function isReturnsWeightByZoneRateTable(
  table: RateTable,
): table is ReturnsWeightByZoneRateTable {
  return table.rateTableType === RateTableType.RETURNS_WEIGHT_BY_ZONE;
}

export function isReturnsDefaultWeightByZoneRateTable(
  table: RateTable,
): table is ReturnsDefaultWeightByZoneRateTable {
  return table.rateTableType === RateTableType.RETURNS_DEFAULT_WEIGHT_BY_ZONE;
}

export function isReturnsDefaultWeightByCountryRateTable(
  table: RateTable,
): table is ReturnsDefaultWeightByCountryRateTable {
  return (
    table.rateTableType === RateTableType.RETURNS_DEFAULT_WEIGHT_BY_COUNTRY
  );
}

export function isReturnsTeamWeightByCountryRateTable(
  table: RateTable,
): table is ReturnsTeamWeightByCountryRateTable {
  return table.rateTableType === RateTableType.RETURNS_TEAM_WEIGHT_BY_COUNTRY;
}

export type WeightAxis = z.infer<typeof weightAxisSchema>;
export type ZoneAxis = z.infer<typeof zoneAxisSchema>;
export type CubicSizeAxis = z.infer<typeof cubicSizeAxisSchema>;
export type PackageAxis = z.infer<typeof packageAxisSchema>;
export type CountryAxis = z.infer<typeof countryAxisSchema>;

const axisSchema = z.union([
  zoneAxisSchema.meta({ privacy: privacy.structured }),
  weightAxisSchema.meta({ privacy: privacy.structured }),
  cubicSizeAxisSchema.meta({ privacy: privacy.structured }),
  packageAxisSchema.meta({ privacy: privacy.structured }),
  countryAxisSchema.meta({ privacy: privacy.structured }),
]);

export type AxisDef = z.infer<typeof axisSchema>;
export type RateTable = z.infer<typeof rateTableSchema>;
export type RateTableDocument = z.infer<typeof RateTableDocumentSchema>;

export interface ShipmentData {
  /** Absent for international shipments — they key on country instead. */
  [AxisKind.ZONE]?: string;
  [AxisKind.WEIGHT]: { unit: WeightUnit; value: number };
  [AxisKind.CUBIC_SIZE]?: { cubic_feet: number };
  [AxisKind.PACKAGE]?: string | null;
  [AxisKind.COUNTRY]?: { country: string; province?: string | null };
}

// A rate table is defined by the carrier, service level, rate table type (weight by zone or size by zone) and value mode (rate vs spread)
export function rateTableDefinitionEqual(
  a: OutboundRateTable,
  b: OutboundRateTable,
) {
  return (
    a.carrier === b.carrier &&
    a.serviceLevel === b.serviceLevel &&
    a.rateTableType === b.rateTableType &&
    a.valueMode === b.valueMode &&
    a.provider === b.provider
  );
}

// Ex: Easypost USPS Ground Advantage Spread
export function stringifiedDefinition(a: OutboundRateTable) {
  const rateTableTypeToLabel = {
    [RateTableType.OUTBOUND_WEIGHT_BY_ZONE]: "Weight",
    [RateTableType.OUTBOUND_SIZE_BY_ZONE]: "Cubic",
    [RateTableType.OUTBOUND_PACKAGE_BY_ZONE]: "Package",
    [RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY]: "Weight by country",
    [RateTableType.OUTBOUND_SIZE_BY_COUNTRY]: "Cubic by country",
    [RateTableType.OUTBOUND_PACKAGE_BY_COUNTRY]: "Package by country",
  };
  const providerToLabel = {
    [RateProvider.EASYPOST]: "Easypost",
    [RateProvider.SOAP_BOX]: "Soapbox",
    [RateProvider.UPS]: "UPS",
    [RateProvider.LANDMARK_GLOBAL]: "Landmark Global",
    [RateProvider.REDO_LANDMARK_GLOBAL]: "Redo Landmark Global",
    [RateProvider.INTERNATIONAL_BRIDGE]: "International Bridge",
    [RateProvider.ETA_EXPRESS]: "ETA Express",
    [RateProvider.TEST_CARRIER]: "Test Carrier",
    [RateProvider.FEDEX]: "FedEx",
    [RateProvider.USPS]: "USPS",
    [RateProvider.DHL_ECOMMERCE]: "DHL eCommerce",
    [RateProvider.CANADA_POST]: "Canada Post",
    [RateProvider.GLOBALE]: "Global-E",
    [RateProvider.TIKTOK]: "TikTok",
    [RateProvider.UNIUNI]: "UniUni",
    [RateProvider.SENDCLOUD]: "Sendcloud",
    [RateProvider.STALLION_EXPRESS]: "Stallion Express",
    [RateProvider.ECOM_EXPRESS]: "Ecom Express",
    [RateProvider.FREIGHTWIRE]: "FreightWire",
    [RateProvider.AMAZON_BUY_SHIPPING]: "Amazon Buy Shipping",
    [RateProvider.CHITCHATS]: "Chit Chats",
  };
  return `${providerToLabel[a.provider]} ${a.carrier} ${a.serviceLevel} ${rateTableTypeToLabel[a.rateTableType]} ${a.valueMode}`;
}

export const defaultNewReturnsRateTable: ReturnsWeightByZoneRateTable = {
  rateTableType: RateTableType.RETURNS_WEIGHT_BY_ZONE,
  valueMode: RateTableValueMode.Spread,
  provider: RateProvider.EASYPOST,
  data: [
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
  ],
  xAxis: {
    values: ["1", "2", "3", "4", "5", "6", "7", "8"],
    kind: AxisKind.ZONE,
  },
  yAxis: {
    values: [
      { minWeight: 0, maxWeight: 0.0625 },
      { minWeight: 0.0625, maxWeight: 0.125 },
      { minWeight: 0.125, maxWeight: 0.1875 },
      { minWeight: 0.1875, maxWeight: 0.25 },
      { minWeight: 0.25, maxWeight: 0.3125 },
      { minWeight: 0.3125, maxWeight: 0.375 },
      { minWeight: 0.375, maxWeight: 0.4375 },
      { minWeight: 0.4375, maxWeight: 0.5 },
      { minWeight: 0.5, maxWeight: 0.5625 },
      { minWeight: 0.5625, maxWeight: 0.625 },
      { minWeight: 0.625, maxWeight: 0.6875 },
      { minWeight: 0.6875, maxWeight: 0.75 },
      { minWeight: 0.75, maxWeight: 0.8125 },
      { minWeight: 0.8125, maxWeight: 0.875 },
      { minWeight: 0.875, maxWeight: 0.9375 },
      { minWeight: 0.9375, maxWeight: 1 },
      { minWeight: 1, maxWeight: 2 },
      { minWeight: 2, maxWeight: 3 },
      { minWeight: 3, maxWeight: 4 },
      { minWeight: 4, maxWeight: 5 },
      { minWeight: 5, maxWeight: 6 },
      { minWeight: 6, maxWeight: 7 },
      { minWeight: 7, maxWeight: 8 },
      { minWeight: 8, maxWeight: 9 },
      { minWeight: 9, maxWeight: 10 },
      { minWeight: 10, maxWeight: 11 },
      { minWeight: 11, maxWeight: 12 },
      { minWeight: 12, maxWeight: 13 },
      { minWeight: 13, maxWeight: 14 },
      { minWeight: 14, maxWeight: 15 },
      { minWeight: 15, maxWeight: 16 },
      { minWeight: 16, maxWeight: 17 },
      { minWeight: 17, maxWeight: 18 },
      { minWeight: 18, maxWeight: 19 },
      { minWeight: 19, maxWeight: 20 },
      { minWeight: 20, maxWeight: 21 },
      { minWeight: 21, maxWeight: 22 },
      { minWeight: 22, maxWeight: 23 },
      { minWeight: 23, maxWeight: 24 },
      { minWeight: 24, maxWeight: 25 },
      { minWeight: 25, maxWeight: 26 },
      { minWeight: 26, maxWeight: 27 },
      { minWeight: 27, maxWeight: 28 },
      { minWeight: 28, maxWeight: 29 },
      { minWeight: 29, maxWeight: 30 },
      { minWeight: 30, maxWeight: 31 },
      { minWeight: 31, maxWeight: 32 },
      { minWeight: 32, maxWeight: 33 },
      { minWeight: 33, maxWeight: 34 },
      { minWeight: 34, maxWeight: 35 },
      { minWeight: 35, maxWeight: 36 },
      { minWeight: 36, maxWeight: 37 },
      { minWeight: 37, maxWeight: 38 },
      { minWeight: 38, maxWeight: 39 },
      { minWeight: 39, maxWeight: 40 },
      { minWeight: 40, maxWeight: 41 },
      { minWeight: 41, maxWeight: 42 },
      { minWeight: 42, maxWeight: 43 },
      { minWeight: 43, maxWeight: 44 },
      { minWeight: 44, maxWeight: 45 },
      { minWeight: 45, maxWeight: 46 },
      { minWeight: 46, maxWeight: 47 },
      { minWeight: 47, maxWeight: 48 },
      { minWeight: 48, maxWeight: 49 },
      { minWeight: 49, maxWeight: 50 },
    ],
    kind: AxisKind.WEIGHT,
    units: WeightUnit.LB,
  },
  defaultValue: "1",
};
